"use client";

import { Plus } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export type StoryRingState =
  | "unseen"
  | "seen"
  | "none"
  | "self-add"
  | "uploading"
  | "upload-error";

interface StoryAvatarRingProps {
  state: StoryRingState;
  /** Outer diameter in px (avatar renders inside the ring gaps). */
  size?: number;
  /**
   * Ring silhouette. "circle" for entity logos/heroes that are themselves
   * round (hiive/event/venue); "squircle" for any person (story tray,
   * profile hero), matching chat avatars.
   */
  shape?: "circle" | "squircle";
  onPress?: () => void;
  /** Secondary press target for the self-add "+" badge. */
  onAddPress?: () => void;
  /**
   * Force the "+" add badge even when a ring is already showing. Instagram
   * keeps "Your story" addable after you've already posted, so the self
   * tray entry passes this whenever the viewer can add another story.
   */
  showAddBadge?: boolean;
  label?: string;
  className?: string;
  /** 0..1 progress fill for the "uploading" state. */
  progress?: number;
  children: ReactNode;
}

/**
 * Instagram-style story ring: conic-gradient for unseen, muted ring for
 * seen, nothing for none, and a "+" badge for the self-add affordance.
 * Children-slot so it wraps any round media (Avatar, hiive logo, banner
 * thumb).
 */
export function StoryAvatarRing({
  state,
  size = 64,
  shape = "circle",
  onPress,
  onAddPress,
  showAddBadge = false,
  label,
  className,
  progress = 0,
  children,
}: StoryAvatarRingProps) {
  const showRing =
    state === "unseen" || state === "seen" || state === "uploading";
  const showAdd = state === "self-add" || showAddBadge;
  const clampedProgress = Math.min(1, Math.max(0, progress));
  // Squircle radii track the chat avatar ratio (14px at 44px ≈ 0.32).
  const outerRadius = shape === "squircle" ? Math.round(size * 0.32) : size / 2;
  const innerRadius =
    shape === "squircle" ? Math.max(outerRadius - 3, 6) : size / 2;

  const media = (
    <span
      className={cn(
        "relative block overflow-hidden",
        showRing && "border-2 border-background",
      )}
      style={{ width: "100%", height: "100%", borderRadius: innerRadius }}
    >
      {children}
    </span>
  );

  return (
    <span
      className={cn(
        "relative block shrink-0 transition-transform active:scale-95",
        className,
      )}
      style={{ width: size, height: size, borderRadius: outerRadius }}
    >
      <button
        type="button"
        onClick={onPress}
        aria-label={label}
        className="block h-full w-full"
        style={{ borderRadius: outerRadius }}
      >
        <span
          className={cn(
            "flex h-full w-full items-center justify-center",
            state === "unseen" &&
              "bg-[conic-gradient(from_210deg,#6a33ff,#ff3b6b,#ffb020,#6a33ff)] p-[2.5px]",
            state === "seen" && "bg-border p-[2px]",
            state === "upload-error" && "bg-destructive p-[2px]",
            (state === "none" || state === "self-add") && "p-0",
          )}
          style={
            state === "uploading"
              ? {
                  borderRadius: outerRadius,
                  background: `conic-gradient(from -90deg, var(--color-brand) ${clampedProgress * 360}deg, var(--color-border) 0deg)`,
                  padding: 2.5,
                }
              : { borderRadius: outerRadius }
          }
        >
          {media}
        </span>
      </button>
      {showAdd && onAddPress ? (
        <button
          type="button"
          aria-label="Add story"
          onClick={(e) => {
            e.stopPropagation();
            onAddPress();
          }}
          className="absolute right-0 bottom-0 flex size-5 items-center justify-center rounded-full border-2 border-background bg-brand text-white"
        >
          <Plus className="h-3 w-3" strokeWidth={3} />
        </button>
      ) : showAdd ? (
        <span
          aria-hidden="true"
          className="absolute right-0 bottom-0 flex size-5 items-center justify-center rounded-full border-2 border-background bg-brand text-white"
        >
          <Plus className="h-3 w-3" strokeWidth={3} />
        </span>
      ) : null}
    </span>
  );
}
